import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Plus, Edit, Trash2, Clock, Zap, Loader2, History, Star, ExternalLink, Users, Repeat } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { it as itLocale } from 'date-fns/locale';
import AutomationFormDialog from './AutomationFormDialog';
import ManualCampaignCard from './ManualCampaignCard';
import { CHANNELS, FILTER_LABELS, REPEAT_OPTIONS } from './triggers';

/** Tutto quello che riguarda UNA tipologia: invio automatico, regole, invio manuale, storico. */
const TriggerPanel = ({ trigger, automations, onRefresh, preset, waConnected, reviewUrl,
  autoSettings, onAutoField, onSaveAuto, savingAuto, history, onCampaignSent }) => {
  const { API, getAuthHeaders } = useAuth();
  const [dialog, setDialog] = useState(null); // {automation} oppure {} per nuova

  const remove = async (id) => {
    if (!window.confirm('Eliminare questa regola?')) return;
    try {
      await axios.delete(`${API}/marketing/automations/${id}`, getAuthHeaders());
      toast.success('Regola eliminata');
      onRefresh();
    } catch {
      toast.error('Eliminazione non riuscita');
    }
  };

  const toggle = async (id) => {
    try {
      await axios.put(`${API}/marketing/automations/${id}/toggle`, {}, getAuthHeaders());
      onRefresh();
    } catch {
      toast.error('Modifica non riuscita');
    }
  };

  const Icon = trigger.icon;

  return (
    <div className="space-y-4" data-testid={`panel-${trigger.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-xl ${trigger.bg} flex items-center justify-center shrink-0`}>
          <Icon className={`w-5 h-5 ${trigger.color}`} />
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold font-outfit">{trigger.label}</h2>
          <p className="text-sm text-muted-foreground">{trigger.description}</p>
        </div>
      </div>

      {trigger.id === 'post_appointment' && (
        <Card data-testid="review-link-card">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Star className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div className="min-w-0 space-y-1">
                <p className="font-medium">Il link recensione che arriva al cliente</p>
                {reviewUrl ? (
                  <a href={reviewUrl} target="_blank" rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline break-all inline-flex items-center gap-1"
                    data-testid="review-link-url">
                    {reviewUrl}<ExternalLink className="w-3.5 h-3.5 shrink-0" />
                  </a>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Non è ancora impostato: aggiungi il link in{' '}
                    <Link to="/google-business" className="text-primary hover:underline">Google Business</Link>{' '}
                    e finirà da solo nei messaggi.
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Su WhatsApp è già il pulsante del modello. Su email e SMS basta il segnaposto{' '}
                  <span className="font-medium text-foreground">{'{recensione}'}</span>, e se lo dimentichi lo
                  aggiungiamo noi in fondo.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invio automatico su WhatsApp (solo dove esiste il modello automatico) */}      {trigger.autoKey && autoSettings && (
        <Card data-testid={`auto-card-${trigger.id}`}>
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium">{trigger.autoTitle}</p>
                  <Badge variant="outline" className="text-[10px] text-green-600">WhatsApp</Badge>
                </div>
                <p className="text-sm text-muted-foreground">{trigger.autoDescription}</p>
              </div>
              <Switch checked={!!autoSettings[trigger.autoKey]}
                onCheckedChange={(v) => onSaveAuto({ [trigger.autoKey]: v })}
                disabled={savingAuto}
                data-testid={`switch-${trigger.autoKey}`} />
            </div>

            {trigger.id === 'birthday' && autoSettings.birthday_enabled && (
              <>
                <div className="grid grid-cols-2 gap-3 pl-4 border-l-2 border-primary/30">
                  <div className="space-y-1">
                    <Label className="text-xs">Sconto compleanno (%)</Label>
                    <Input type="number" value={autoSettings.birthday_discount_pct || ''}
                      onChange={(e) => onAutoField('birthday_discount_pct', e.target.value)}
                      data-testid="input-birthday-discount" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Valido per (giorni)</Label>
                    <Input type="number" value={autoSettings.birthday_valid_days || ''}
                      onChange={(e) => onAutoField('birthday_valid_days', e.target.value)}
                      data-testid="input-birthday-days" />
                  </div>
                </div>
                <Button onClick={() => onSaveAuto()} disabled={savingAuto} variant="outline"
                  className="min-h-[44px]" data-testid="save-auto-settings-btn">
                  {savingAuto && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Salva
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Regole personalizzate (WhatsApp, email, SMS) */}
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 pb-3">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-outfit">Le tue regole</CardTitle>
            <CardDescription>
              Su WhatsApp usano il modello approvato (compili solo i campi), su email e SMS il testo lo scrivi tu.
            </CardDescription>
          </div>
          <Button onClick={() => setDialog({})} className="w-full sm:w-auto min-h-[44px]"
            data-testid={`add-automation-${trigger.id}`}>
            <Plus className="w-4 h-4 mr-2" />Nuova regola
          </Button>
        </CardHeader>
        <CardContent>
          {automations.length > 0 ? (
            <div className="space-y-3">
              {automations.map((auto) => {
                const channel = CHANNELS.find((c) => c.id === auto.channel);
                const hours = (auto.delay_days || 0) * 24 + (auto.delay_hours || 0);
                return (
                  <div key={auto.id} className={`grid grid-cols-[1fr_auto] gap-x-3 gap-y-2 p-3 rounded-lg border ${auto.is_active ? 'border-border' : 'border-muted bg-muted/30'}`}
                    data-testid={`automation-item-${auto.id}`}>
                    <div className="min-w-0">
                      <p className="font-medium break-words">{auto.name || trigger.label}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          <span className={channel?.color}>{channel?.label || auto.channel}</span>
                        </Badge>
                        {(trigger.id === 'appointment_reminder' || trigger.id === 'post_appointment') && (
                          <Badge variant="outline" className="text-xs gap-1">
                            {hours === 0
                              ? <><Zap className="w-3 h-3 text-primary" />Subito</>
                              : <><Clock className="w-3 h-3" />{hours}h {trigger.id === 'appointment_reminder' ? 'prima' : 'dopo'}</>}
                          </Badge>
                        )}
                        {trigger.id === 'promotional' && auto.scheduled_for && (
                          <Badge variant="outline" className="text-xs gap-1">
                            <Clock className="w-3 h-3" />
                            {format(parseISO(auto.scheduled_for), "d MMM 'alle' HH:mm", { locale: itLocale })}
                          </Badge>
                        )}
                        {trigger.id === 'promotional' && auto.repeat && auto.repeat !== 'once' && (
                          <Badge variant="outline" className="text-xs gap-1" data-testid={`repeat-badge-${auto.id}`}>
                            <Repeat className="w-3 h-3" />
                            {REPEAT_OPTIONS.find((r) => r.id === auto.repeat)?.label}
                          </Badge>
                        )}
                        {trigger.id === 'promotional' && (
                          <Badge variant="outline" className="text-xs gap-1" data-testid={`filter-badge-${auto.id}`}>
                            <Users className="w-3 h-3" />
                            {FILTER_LABELS[auto.recipient_filter || 'all']}
                          </Badge>
                        )}
                        <span className="text-xs text-muted-foreground">{auto.stats?.sent || 0} inviati</span>
                        {(auto.stats?.failed || 0) > 0 && (
                          <span className="text-xs text-destructive" data-testid={`failed-count-${auto.id}`}>
                            {auto.stats.failed} non consegnati
                          </span>
                        )}
                      </div>
                    </div>
                    <Switch checked={auto.is_active} onCheckedChange={() => toggle(auto.id)}
                      data-testid={`toggle-auto-${auto.id}`} />
                    <div className="col-span-2 flex justify-end gap-2 border-t pt-2">
                      <Button variant="outline" size="sm" className="min-h-[40px] sm:w-9 sm:h-9 sm:p-0"
                        onClick={() => setDialog({ automation: auto })} data-testid={`edit-auto-${auto.id}`}>
                        <Edit className="w-4 h-4 mr-2 sm:mr-0" /><span className="sm:hidden">Modifica</span>
                      </Button>
                      <Button variant="outline" size="sm" className="min-h-[40px] sm:w-9 sm:h-9 sm:p-0 text-destructive hover:text-destructive"
                        onClick={() => remove(auto.id)} data-testid={`delete-auto-${auto.id}`}>
                        <Trash2 className="w-4 h-4 mr-2 sm:mr-0" /><span className="sm:hidden">Elimina</span>
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Nessuna regola per ora. {trigger.autoKey
                ? 'Se qui sopra lasci acceso l\'invio automatico, il messaggio su WhatsApp parte comunque da solo. Email e SMS invece partono solo con una regola.'
                : 'Creane una per iniziare.'}
            </p>
          )}
        </CardContent>
      </Card>

      {preset && <ManualCampaignCard preset={preset} waConnected={waConnected} onSent={onCampaignSent} />}

      {history.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-outfit flex items-center gap-2">
              <History className="w-4 h-4" />Invii fatti
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {history.slice(0, 5).map((h) => (
              <div key={h.id} className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg bg-muted/40"
                data-testid={`history-${h.id}`}>
                <div className="min-w-0">
                  <p className="text-sm">
                    {format(parseISO(h.created_at), "d MMM yyyy 'alle' HH:mm", { locale: itLocale })}
                  </p>
                  <p className="text-xs text-muted-foreground">{FILTER_LABELS[h.filter] || h.filter}</p>
                </div>
                <div className="flex gap-2">
                  <Badge variant="default">{h.sent_count} inviati</Badge>
                  {h.failed_count > 0 && <Badge variant="destructive">{h.failed_count} non riusciti</Badge>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      {dialog && (
        <AutomationFormDialog
          trigger={trigger}
          preset={preset}
          reviewUrl={reviewUrl}
          automation={dialog.automation}
          open
          onOpenChange={(o) => { if (!o) setDialog(null); }}
          onSaved={onRefresh}
        />
      )}
    </div>
  );
};

export default TriggerPanel;
